import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { openGlobalError } from '../services/errorService';

const baseURL = process.env.REACT_APP_API_BASE_URL || '';

const CONNECTION_TITLE = 'Falha de conexão';
const CONNECTION_MESSAGE = 'Verifique sua conexão com a internet e tente novamente.';
const SAVE_ERROR_TITLE = 'Erro ao salvar informações';
const SAVE_ERROR_MESSAGE = 'Não foi possível salvar os dados.';
const SCOPED_SAVE_METHODS = ['post', 'patch', 'put'];

const buildNormalizedError = ({
  title,
  message,
  retryable,
  type,
  handled = false,
  showModal = false,
  signature = '',
  status = null,
  apiCode = null,
  method = '',
}) => ({
  title,
  message,
  retryable,
  type,
  handled,
  showModal,
  signature,
  status,
  apiCode,
  method,
});

export const buildSaveRequestConfig = (config = {}) => ({
  ...config,
  preferApiMessage: config.preferApiMessage !== false,
});

const getRequestMethod = (error) => String(error?.config?.method || '').toLowerCase();

const isScopedSaveRequest = (error) => SCOPED_SAVE_METHODS.includes(getRequestMethod(error));

const getRequestPolicy = (config) => ({
  suppressErrorModal: Boolean(config?.suppressErrorModal),
  retryPending: Boolean(config?.retryPending),
  preferApiMessage: config?.preferApiMessage !== false,
});

const getApiMessage = (error) => {
  const apiMessage = error?.response?.data?.message || error?.response?.data?.mensagem;

  return typeof apiMessage === 'string' ? apiMessage.trim() : '';
};

const buildErrorSignature = ({
  type,
  title,
  message,
  status,
  apiCode,
  method,
  showModal,
}) => [
  method || 'unknown',
  type || 'unknown',
  status ?? 'na',
  apiCode || 'na',
  showModal ? 'visible' : 'hidden',
  title || '',
  message || '',
].join('::');

const finalizeNormalizedError = (error, normalizedError) => {
  const method = getRequestMethod(error);
  const status = error?.response?.status ?? null;
  const apiCode = error?.response?.data?.code ?? null;
  const eligibleRequest = isScopedSaveRequest(error);
  const {
    suppressErrorModal,
    retryPending,
  } = getRequestPolicy(error?.config);
  const showModal = eligibleRequest && !suppressErrorModal && !retryPending;

  return buildNormalizedError({
    ...normalizedError,
    handled: Boolean(normalizedError.handled),
    showModal,
    status,
    apiCode,
    method,
    signature: buildErrorSignature({
      ...normalizedError,
      status,
      apiCode,
      method,
      showModal,
    }),
  });
};

const isNetworkOrOfflineError = (error) => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }

  const hasResponse = Boolean(error?.response);

  if (!hasResponse) {
    const isNetworkCode = error?.code === 'ERR_NETWORK';
    const isNetworkMessage = error?.message === 'Network Error';
    const hasRequestWithoutResponse = Boolean(error?.request || error?.config);

    if (isNetworkCode || isNetworkMessage || hasRequestWithoutResponse) {
      return true;
    }
  }

  return false;
};

export const normalizeApiError = (error) => {
  const apiCode = error?.response?.data?.code;
  const apiMessage = getApiMessage(error);
  const status = error?.response?.status;
  const eligibleRequest = isScopedSaveRequest(error);
  const { preferApiMessage } = getRequestPolicy(error?.config);

  if (isNetworkOrOfflineError(error)) {
    return finalizeNormalizedError(error, buildNormalizedError({
      title: CONNECTION_TITLE,
      message: CONNECTION_MESSAGE,
      retryable: true,
      type: 'network',
    }));
  }

  if (apiCode === 'VALIDATION_ERROR') {
    return finalizeNormalizedError(error, buildNormalizedError({
      title: 'Dados inválidos',
      message: apiMessage || 'Verifique os campos informados.',
      retryable: false,
      type: 'validation',
    }));
  }

  if (apiCode === 'PHONE_ALREADY_EXISTS') {
    return finalizeNormalizedError(error, buildNormalizedError({
      title: 'Telefone já utilizado',
      message: apiMessage || 'Este telefone já foi utilizado anteriormente.',
      retryable: false,
      type: 'validation',
    }));
  }

  if (apiCode === 'SAVE_ERROR') {
    return finalizeNormalizedError(error, buildNormalizedError({
      title: SAVE_ERROR_TITLE,
      message: apiMessage || SAVE_ERROR_MESSAGE,
      retryable: true,
      type: 'save_error',
    }));
  }

  if (apiCode === 'FILE_LOAD_ERROR') {
    return finalizeNormalizedError(error, buildNormalizedError({
      title: 'Erro ao carregar arquivo',
      message: apiMessage || 'Não foi possível carregar o arquivo.',
      retryable: true,
      type: 'file_load_error',
    }));
  }

  if (apiCode === 'CONNECTION_ERROR' || status === 503) {
    return finalizeNormalizedError(error, buildNormalizedError({
      title: 'Falha de conexão',
      message: apiMessage || 'Serviço temporariamente indisponível.',
      retryable: true,
      type: 'service_unavailable',
    }));
  }

  if (status === 404) {
    return finalizeNormalizedError(error, buildNormalizedError({
      title: 'Informação não encontrada',
      message: apiMessage || 'Não foi possível localizar a informação solicitada.',
      retryable: false,
      type: 'not_found',
    }));
  }

  if (eligibleRequest) {
    return finalizeNormalizedError(error, buildNormalizedError({
      title: SAVE_ERROR_TITLE,
      message: preferApiMessage && apiMessage ? apiMessage : SAVE_ERROR_MESSAGE,
      retryable: true,
      type: 'save_error',
    }));
  }

  if (status >= 500) {
    return finalizeNormalizedError(error, buildNormalizedError({
      title: 'Erro interno',
      message: apiMessage || 'O servidor apresentou um problema.',
      retryable: true,
      type: 'server',
    }));
  }

  return finalizeNormalizedError(error, buildNormalizedError({
    title: 'Erro inesperado',
    message: apiMessage || 'Ocorreu um erro inesperado.',
    retryable: true,
    type: 'unknown',
  }));
};

let refreshSubscribers = [];
let refreshPromise = null;

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

const axiosInstance = axios.create({
  baseURL,
});

const refreshToken = async () => {
  if (!refreshPromise) {
    refreshPromise = axiosInstance
      .post('login/refresh-token', {
        refreshToken: localStorage.getItem('refreshToken'),
      })
      .then((response) => {
        const { token, refreshToken: newRefreshToken } = response.data;
        const decodedToken = jwtDecode(token);
        const departments = decodedToken?.departments;
        localStorage.setItem('token', token);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('departments', departments);
        onRefreshed(token);
        return token;
      })
      .catch((error) => {
        console.error('Token refresh failed:', error);
        localStorage.clear();
        window.location.href = '/login';
        throw error;
      })
      .finally(() => {
        refreshPromise = null; // Reset the promise when refresh is done
      });
  }
  return refreshPromise;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const errorType = error.response?.data?.type;
    const errorCode = error.response?.data?.code;
    const isRefreshRequest = originalRequest?.url?.includes('login/refresh-token');

    if (errorType === 'PASSWORD_EXPIRED' || errorCode === 'PASSWORD_EXPIRED') {
      console.log('AUTH ERROR:', status, error.response?.data);
      return Promise.reject(error);
    }

    const isUnauthorized = status === 401;
    if (
      isUnauthorized
      && !originalRequest._retry
      && !isRefreshRequest
    ) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshToken();

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    if (status === 401) {
      localStorage.clear();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (status === 403) {
      return Promise.reject(error);
    }

    const normalizedError = error.normalizedError || normalizeApiError(error);
    error.normalizedError = normalizedError;

    const isScopedSaveError = SCOPED_SAVE_METHODS.includes(normalizedError.method);
    const shouldDispatchModal = isScopedSaveError
      ? normalizedError.showModal
      : normalizedError.type === 'network';

    // Não mostrar modal para erros de GET ou 404 (informação não encontrada)
    const shouldSkipModal = normalizedError.method === 'get' || normalizedError.type === 'not_found';

    if (!normalizedError.handled && shouldDispatchModal && !shouldSkipModal) {
      openGlobalError(normalizedError);
      normalizedError.handled = true;
    }

    return Promise.reject(error);
  },
);

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

export default axiosInstance;
